<template>
  <div class="search" v-bind:class="{'iphone': iOS}" v-hotkey="keymap">
    <div class="container pt-3 pt-md-0">
      <spinner v-if="running"></spinner>
      <div class="raised-frame px-4 pt-3 pb-4 w-75 ml-auto mr-auto mt-4 bg-white h-100">
        <h2 class="mb-0">
          <img src="@/assets/text-icon.png" alt />
          {{hebrew ? 'הזן כאן טקסט על מנת למצוא מקבילות' : 'Enter text here to locate parallels'}}
        </h2>
        <div
          v-bind:class="[emptyText ? 'border-danger' : 'border-dark', 'bg-white text-wrap border rounded w-100 mt-md-2 p-2 position-relative']"
        >
          <div            
            class="erase position-absolute p-2"
            id = "del-btn"
            v-if="searchText && searchText !== ''"
            @click="clearText"
          >
            <span aria-hidden="true">&times;</span>
          </div>
          <ResetDialog v-model="showReset" v-if="showReset" v-on:dialogClick="clearText"></ResetDialog>
          <textarea
            id="textEntryArea"
            ref="textArea"
            @input="checkText"
            @focus="focusedEvent"
            @focusout="blurEvent"
            class="f-narkis w-100 border-0 pl-3 text-right"
            v-model="searchText"
          ></textarea>
          <div class="d-flex justify-content-between">          
            <form ref="fileForm" class="mt-2 d-none d-md-block">
              <label for="file-input" class="file-button btn btn-secondary m-0">
                <i-file></i-file> {{this.hebrew ? 'העלה קובץ' : 'Upload file'}}
              </label>
              <input 
              type="file" name="name"
              accept=".txt, .doc, .docx"
              id="file-input" class="d-none" ref="inputEl" 
              @change="loadInputtedFile">
            </form>
            <div>
              <small 
                v-if="searchText.length>1"
                class="char-count d-inline-block mx-3 text-muted" 
                style="vertical-align:middle;"
                :class="{'text-danger':searchText.length>50000}">
                {{searchText.length}}/50000                
              </small>
              <b-button
                id="findInstancesBttn"
                variant="primary"
                size="lg"            
                @click="search"                
                >{{hebrew ? 'מצא מקבילות' : 'Find Parallels'}}
              </b-button>
            </div>            
          </div>           
        </div>
        <server-failed-popup v-if="failed" v-model="failed" :msg="serverErrorMessage"/>
        <b-modal
          id="text-exceeded-modal"
          centered
          size="sm"
          body-class="text-center px-0"
          footer-class="border-top"
          hide-header
          v-model="showTextTooLongPrompt"
          v-if="showTextTooLongPrompt"
          ok-only
          :ok-title="hebrew ? 'בסדר, הבנתי' : 'OK, I understand'"
          @ok="showTextTooLongPrompt = false"
        >
          {{hebrew ? 'אפשר להעלות טקסט של עד 50,000 תווים' : 'Text is limited to 50,000 characters'}}
        </b-modal>
      </div>
    </div>
  </div>
</template>

<script>
import { Actions } from "@/store/stateChanges"
import {RunStates} from '@/store/runStates'
import Spinner from "@/components/spinner"
import ResetDialog from "@/components/ResetDialog"
import openXml from 'openxml'
import base64js from 'base64-js'
import ServerFailedPopup from "@/components/ServerFailedPopup"

var READABLE_TYPES = {
  text: 'text/plain',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
}
export default {
  name: "Search",
  props: {},
  components: { Spinner, ResetDialog, ServerFailedPopup},
  data() {
    return {
      searchText: '',
      emptyText: false,
      showTextTooLongPrompt: false,
      notHebrew: false,
      showReset: false,
      DontAskAgain: false,
      iOS: false
    };
  },
  mounted () {
    this.$store.commit('FOOTNOTES_NOT_RUN')
    this.DontAskAgain = localStorage.NOT_ASK
    var isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    this.iOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (!this.searchText && !isMobile) {
      this.$refs.textArea.focus();
    }
    if (isMobile && !this.iOS) {
      const orgTextHeight = document.getElementById("textEntryArea").clientHeight
      window.addEventListener("resize", function() {
        // If the current active element is a text input, we can assume the soft keyboard is visible.
        if (document.getElementById("textEntryArea").clientHeight < orgTextHeight) {
          document.body.classList.add("search-focused")
        } else {
          document.body.classList.remove("search-focused")
        }
      });
    }
  },
  computed: {
    keymap () {
      return {
        'ctrl+enter': () => { 
          this.search()
        }
      }
    },    
    hebrew() {
      return this.$settings.hebrew;
    },
    running () {
      return this.$store.state.serverState ===  RunStates.RUNNING
    },
    serverErrorMessage () {
      return this.$store.state.serverState === RunStates.TIMED_OUT ? 
        //this.hebrew ? 'יש להזין טקסט יותר קצר' : 'Please upload a shorter text.' :
        this.hebrew ? 'נסו שוב בעוד מספר דקות' : 'Please try again.' :
        null
    },    
    failed: {
      get () {
        return this.$store.state.serverState === RunStates.FAILED || 
          this.$store.state.serverState === RunStates.TIMED_OUT
      },
      set () {
        this.$store.commit('FOOTNOTES_NOT_RUN')        
      }
    },
   
    data () {
      return this.$store.state.selectedResults;
    }
  }, 
  methods: {
    focusedEvent () {
      document.body.classList.add("search-focused")
      this.$refs.textArea.focus();
    },
    blurEvent () {
      if (this.iOS) {
         setTimeout(function() {
          document.activeElement.blur()
          document.body.classList.remove("search-focused")          
         }, 200);
       }
    },
    loadWordDocFromArrayBuffer (contents) {
      const doc = new openXml.OpenXmlPackage(contents)
      const encoded = base64js.fromByteArray(new Uint8Array(contents))
      const source = {
        fileBase64Contents: encoded,
        type: 'docx',
        runArray: doc.mainDocumentPart().getXDocument().descendants(openXml.W.r).toArray().map(o => o.value),
        wordToRunAndPosMap: []
      }
      const docText = doc.mainDocumentPart().getXDocument().descendants(openXml.W.p).toJoinedString('\n', x => x.value)
      return { source: source, text: docText }
    },    
    loadInputtedFile () {
      var f = this.$refs.inputEl.files[0]   
      // if there is a file available and it is a type we support
      if (f && Object.values(READABLE_TYPES).includes(f.type)) {
        // set up a FileReader, which reads files asynchronously. It fires an onload event when the file is read.
        var r = new FileReader()
        r.onload = (e) => {
          var contents = e.target.result
          var docText = contents
          if (f.type === READABLE_TYPES.docx) {         
            const results = this.loadWordDocFromArrayBuffer(contents)
            docText = results.text
          } 
          // saves information about the source file
          // updates the text in the store - this is a separate commit because it also applies to direct text entry, not just files
          this.searchText = docText
          this.$refs.fileForm.reset()
        }
        // actually start the file read, which will eventually fire the onload event
        // readAsArrayBuffer is used for Word files
        if (f.type === READABLE_TYPES.text) { r.readAsText(f) } else r.readAsArrayBuffer(f)
      } else { 
        alert("Failed to load file")
      }
    },    
    clearErrors () {
      this.shortText = false;
      this.longText = false;
      this.notHebrew = false;
    },
    clearText () { //value former param
      this.DontAskAgain = true;
      this.$store.commit("SET_TEXT_FOR_API_CALL", "");
      this.searchText = "";
      this.$refs.textArea.focus();
      this.clearErrors();
      //}
    },
    openReset () {
      if (this.iOS)
        document.body.classList.remove("search-focused")
      if (!this.DontAskAgain) this.showReset = true;
      else {
        this.clearText(true);
      }
    },
    search () {
      document.body.classList.remove("search-focused")
      // this.searchText = this.searchText.replace(/\s+/g, ' ').trim()
      if (this.searchText.length > 0 && this.searchText.length <= 50000) {                
        this.$store.commit("SET_TEXT_FOR_API_CALL", this.searchText);
        //localStorage.SAVE_TEXT = this.searchText;
       /*  if (
          !this.searchText
            .split("")
            .filter(c => "קראטוןםפשדגכעיחלךףזסבהנמצתץ".includes(c)).length
        ) {
          this.notHebrew = true;
        }  */
          
          this.$store.dispatch(Actions.RUN_FOOTNOTES);
        
      } else {
        //this.emptyText = true;
        if (this.searchText.length > 50000) {
          this.showTextTooLongPrompt = true
        }
        
      }
    },
    checkText () {
      this.emptyText = this.searchText === "";
    }
  }
};
</script>
<style scoped lang=scss>
h2 {
  direction: ltr;
}
.he h2 {
  direction: rtl;
}
.container {
  height: calc(100vh - 130px);
  position: relative;
  z-index: 2;
}
.promotions-displayed .container{
  height: calc(100vh - 160px);
}
.raised-frame {
  min-height: 200px;
  overflow-y: auto;
}
.erase {
  left: 5px;
  top: 0;
  font-size: 20px;
  @media only screen and (max-width: 991px) {
    span {
      font-size: 34px;
    }
  }
}
.text-wrap {
  height: calc(100% - 45px);

  &:focus-within {
    border-color: var(--primary) !important;
  }
}

textarea {
  height: calc(100% - 45px);
  direction: rtl;
  font-size: 18px;
  overflow-y: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
  &::-webkit-scrollbar {
    width: 0px;
    background: transparent; /* Chrome/Safari/Webkit */
  }
  resize: none;
  &:focus {
    outline: none;
  }
}
</style>
<style lang="scss">
#text-exceeded-modal {
  button {
    width: 100%;
  }
}
@media only screen and (max-width: 992px) {
  .search .raised-frame {
    width: 100% !important;
  }
}
@media only screen and (max-width: 767px) {

  body {

    .search {
      .container {
        height: 100% !important;
      }
      .text-wrap {
        height: calc(100% - 8px);
        height: 100%;
      }
      .raised-frame {
        margin: 0 !important;
        padding: 0 !important;

        h2 {
          display: none;
        }
        height: calc(100vh - 150px)!important;
        min-height: calc(100vh - 150px)!important;
      }
      &.iphone {
        .raised-frame {
          height: calc(100vh - 228px)!important;
          min-height: calc(100vh - 228px)!important;
        }
      }      
    }
    &.search-focused {    
      .search {
    
        &.iphone {
          .raised-frame {
            min-height: 40vh !important;
            height: 40vh !important;
          }
        }
      }
    }
  }
}
</style>
